import createClient from 'openapi-fetch'
import type { Middleware } from 'openapi-fetch'
import type { paths } from '@/lib/generated/api-types'
import { getUserManager } from '@/lib/auth-oidc'
import { API_BASE_URL } from '@/lib/config'

// Custom error class for API errors
export class ApiError extends Error {
  status: number
  detail: string
  requestId?: string

  constructor(status: number, detail: string, requestId?: string) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
    this.requestId = requestId
  }
}

// Promise-based lock for coordinating 401 retry across concurrent requests
// All requests that hit 401 will wait for this single refresh attempt
let refreshAndRetryPromise: Promise<void> | null = null

// Store request bodies before they're consumed (for retry support)
const requestBodyMap = new WeakMap<Request, BodyInit | null>()

// Helper to parse error response body
async function parseErrorBody(
  response: Response,
): Promise<{ detail: string; requestId?: string }> {
  try {
    const errorBody = await response.json()
    if (
      typeof errorBody === 'object' &&
      errorBody !== null &&
      'detail' in errorBody
    ) {
      return {
        detail: String(errorBody.detail),
        requestId: errorBody.request_id ?? undefined,
      }
    }
  } catch {
    // JSON parsing failed, use statusText
  }
  return { detail: response.statusText }
}

// Helper to get current access token
export async function getAccessToken(): Promise<string | null> {
  if (typeof window === 'undefined') {
    return null
  }

  const userManager = getUserManager()
  if (!userManager) {
    return null
  }

  const user = await userManager.getUser()
  return user?.access_token ?? null
}

// Helper to check if we have a session that can attempt token refresh
// Unlike isAuthenticated(), this returns true even if access token is expired,
// as long as we have a refresh_token to attempt renewal
async function hasSession(): Promise<boolean> {
  if (typeof window === 'undefined') {
    return false
  }

  const userManager = getUserManager()
  if (!userManager) {
    return false
  }

  const user = await userManager.getUser()
  return !!user && !!user.refresh_token
}

// Helper to refresh token silently
async function refreshTokenSilently(): Promise<string> {
  const userManager = getUserManager()
  if (!userManager) {
    throw new Error('UserManager not available')
  }

  const user = await userManager.signinSilent()
  if (!user?.access_token) {
    throw new Error('Silent refresh did not return a token')
  }

  console.log('[API Client] Token refresh successful')
  return user.access_token
}

// Request ID middleware to generate and attach X-Request-ID to every request
const requestIdMiddleware: Middleware = {
  onRequest({ request }) {
    request.headers.set('X-Request-ID', crypto.randomUUID())
    return request
  },
}

// Auth middleware to inject Bearer token
const authMiddleware: Middleware = {
  async onRequest({ request, options }) {
    // Store the body for potential retry (before it gets consumed)
    const body = (options as { body?: BodyInit | null }).body ?? null
    requestBodyMap.set(request, body)

    // Check if user has a session (for logging purposes)
    const hadSession = await hasSession()

    const token = await getAccessToken()
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`)
    } else if (hadSession) {
      // Had session but getAccessToken returned null (token likely expired)
      // Let the request proceed - errorMiddleware will handle 401 retry
      console.warn(
        '[API Client] Could not get token but user has session - proceeding without token',
      )
    }
    return request
  },
}

// Error handling middleware with 401 retry logic
// Uses a shared promise to coordinate concurrent 401 handling
const errorMiddleware: Middleware = {
  async onResponse({ response, request }) {
    if (!response.ok) {
      // Handle 401 - attempt token refresh and retry
      if (response.status === 401) {
        console.log('[API Client] Got 401 response, checking auth state')

        // Check if we have a session with refresh token to attempt refresh
        const session = await hasSession()
        if (!session) {
          console.warn(
            '[API Client] No session with refresh token, cannot refresh',
          )
          throw new ApiError(401, 'Authentication required - please log in')
        }

        // If a refresh is already in progress, wait for it
        if (refreshAndRetryPromise) {
          console.log('[API Client] Waiting for ongoing token refresh')
          await refreshAndRetryPromise
          // After refresh completes, retry this request with new token
          return retryRequestWithCurrentToken(request)
        }

        // Start a new refresh attempt - other concurrent 401s will wait on this
        console.log('[API Client] Starting token refresh')
        refreshAndRetryPromise = (async () => {
          try {
            await refreshTokenSilently()
          } catch (error) {
            console.error('[API Client] Token refresh failed:', error)
            throw new ApiError(401, 'Session expired - please log in again')
          } finally {
            refreshAndRetryPromise = null
          }
        })()

        try {
          await refreshAndRetryPromise
          // Refresh succeeded, retry the original request
          return retryRequestWithCurrentToken(request)
        } catch (error) {
          if (error instanceof ApiError) {
            throw error
          }
          throw new ApiError(401, 'Session expired - please log in again')
        }
      }

      // Parse error response for non-401 errors
      const { detail, requestId: bodyRequestId } =
        await parseErrorBody(response)
      const requestId =
        bodyRequestId ?? request.headers.get('X-Request-ID') ?? undefined
      const error = new ApiError(response.status, detail, requestId)

      throw error
    }
    return response
  },
}

// Retry a request with the current (refreshed) token
async function retryRequestWithCurrentToken(
  originalRequest: Request,
): Promise<Response> {
  const token = await getAccessToken()
  if (!token) {
    throw new ApiError(401, 'No access token available')
  }

  // Get the stored body (before it was consumed)
  const storedBody = requestBodyMap.get(originalRequest)

  // Create a new request with the fresh token
  const retryRequest = new Request(originalRequest.url, {
    method: originalRequest.method,
    headers: new Headers(originalRequest.headers),
    body: storedBody,
    credentials: originalRequest.credentials,
    mode: originalRequest.mode,
    cache: originalRequest.cache,
    redirect: originalRequest.redirect,
    referrer: originalRequest.referrer,
    integrity: originalRequest.integrity,
  })
  retryRequest.headers.set('Authorization', `Bearer ${token}`)

  const retryResponse = await fetch(retryRequest)

  if (!retryResponse.ok) {
    const { detail, requestId: bodyRequestId } =
      await parseErrorBody(retryResponse)
    const requestId =
      bodyRequestId ?? originalRequest.headers.get('X-Request-ID') ?? undefined
    throw new ApiError(retryResponse.status, detail, requestId)
  }

  return retryResponse
}

// Create the typed client
export const client = createClient<paths>({ baseUrl: API_BASE_URL })

// Register middlewares (order: request ID first, then auth, then error handling)
client.use(requestIdMiddleware)
client.use(authMiddleware)
client.use(errorMiddleware)

// Re-export types for convenience
export type { paths, components } from '@/lib/generated/api-types'
